import React, { useEffect, useRef, useState } from 'react';
import { isNullOrUndefined, showAlertSnackBar } from 'utils/Global';
import { IStyle } from 'Styles';
import { SelectNameValue } from 'business/models/CustomModels';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  selectExperimentLayers,
  selectSelectedLayerId,
} from 'redux/selectors/ExperimentStateSelector';
import { LayerTypeDTO } from 'business/models/Models';
import { applyDoeIngredients } from 'redux/reducers/experimentDataReducer';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { RMSvgIconProps } from 'components/customComponents/CommonComponents/RMSvgIcon';
import ImportDoeView from './ImportDoeView';
import { useModalHandlers } from 'components/modal';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { FmlxModal } from 'fmlx-common-ui';
import { useIsScreenInUse } from 'redux/hooks/common';
import { validateColumnTypes } from './Utils';
import AlertBoxBody from './AlertBoxBody';

const concUnits: SelectNameValue[] = [
  {
    value: 1,
    name: '%v/v',
  },
  {
    value: 2,
    name: '%w/v',
  },
  {
    value: 3,
    name: 'mM',
  },
  {
    value: 4,
    name: 'M',
  },
  {
    value: 5,
    name: 'pH',
  },
];

const importDoeFileFormatErrorMessage: string =
  'Please make sure import DOE file type is .csv and follow the DOE format file.';

const ImportDoe = () => {
  const inputFileRef = useRef(null);
  const [doePreviewTableData, setDoePreviewTableData] = useState([]);
  const [matchedIngredients, setMatchedIngredients] = useState([]);
  const _layers = useRMSelector(selectExperimentLayers);
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const _selectedLayer = _layers.find((l) => l.id === _selectedLayerId);
  const callPreserveUndoableState = usePreserveExperiment();
  const dispatch = useRMDispatch();
  const { showModal, hideModal } = useModalHandlers();
  const isScreenInUse: boolean = useIsScreenInUse();

  const importIconProps: RMSvgIconProps = {
    name: 'Upload',
    fill: 'white',
  };

  useEffect(() => {
    if (doePreviewTableData.length > 0) {
      showModal({
        body: (
          <ImportDoeView
            doePreviewTableData={doePreviewTableData}
            setDoePreviewTableData={setDoePreviewTableData}
            matchedIngredients={matchedIngredients}
            setMatchedIngredients={setMatchedIngredients}
          />
        ),
        onOk: importIngredients,
        heading: 'Import DOE',
        size: FmlxModal.Size.QUADRUPLE_EXTRA_LARGE,
        dialogClassName: 'min-w-[1302px] m-0',
        okButtonText: 'Import',
        isCancelOnly: false,
      });
    }
  }, [doePreviewTableData]);

  const convertCsvFileData = (csvFileData) => {
    const doeTable: any[] = [];

    let headerCount = 0;
    // eslint-disable-next-line
    for (const headerRow of csvFileData[0]) {
      const doeTableRow: any[] = [];
      for (const row of csvFileData) {
        if (row[headerCount] == 'pH') {
          doeTableRow.push(row[headerCount] + '@Col-' + (headerCount + 1).toString());
        } else {
          doeTableRow.push(row[headerCount]);
        }
      }

      const rowObject: any = {
        name: doeTableRow[0],
        matchedIngredient: null,
        selectedUnit: concUnits[0],
        values: doeTableRow,
      };

      doeTable.push(rowObject);
      headerCount = headerCount + 1;
    }

    setDoePreviewTableData(doeTable);
  };

  function addAttachmentHandler() {
    if (!isNullOrUndefined(inputFileRef)) {
      inputFileRef.current.click();
    }
  }

  async function attachmentChangedHandler(e) {
    const file = e.target.files[0];

    if (!file.type.includes('csv')) {
      showModal({
        body: (
          <AlertBoxBody
            type="error"
            title="Wrong format file"
            message={importDoeFileFormatErrorMessage}
          />
        ),
        onOk: hideModal,
        heading: 'Import DOE Data',
        okButtonText: 'CLOSE',
        isOkOnly: true,
        id: 'importDoeWarningModal',
        size: FmlxModal.Size.MEDIUM,
      });
      return;
    }

    const reader = new FileReader();

    reader.onload = function (e) {
      const contents = e.target.result;
      // You can process the CSV contents here
      parseDoeCSVFile(contents);
    };

    reader.readAsText(file);
    e.target.value = '';
  }

  const importIngredients = () => {
    applyIngredient();
  };

  async function parseDoeCSVFile(csvText) {
    const doeTable: any = [];
    const lines = csvText.split('\n');

    lines.forEach((line) => {
      line = line.replace(/\r/g, '');

      if (line != '') {
        const doeRow: any = [];
        const values = line.split(',');

        values.forEach((value) => {
          doeRow.push(value);
        });

        doeTable.push(doeRow);
      }
    });

    const error = await validateColumnTypes(doeTable);

    if (error) {
      showModal({
        body: (
          <AlertBoxBody
            type="error"
            title="Wrong format file"
            message={importDoeFileFormatErrorMessage}
          />
        ),
        onOk: hideModal,
        heading: 'Error',
        okButtonText: 'CLOSE',
        isOkOnly: true,
      });
      return;
    }

    await convertCsvFileData(doeTable);
  }

  const setShowModalFalse = () => {
    hideModal();
  };

  function applyIngredient() {
    if (!_selectedLayer) {
      return;
    }
    callPreserveUndoableState();

    if (_selectedLayer.type === LayerTypeDTO.Grid) {
      applyDoeIngredientsData();
    }
  }

  function applyDoeIngredientsData() {
    for (const tableRow of doePreviewTableData) {
      if (!tableRow.matchedIngredient) {
        showAlertSnackBar(
          EAlertType.WARNING,
          ESnackBarPosition.TOP,
          'Please match all ingredients.',
        );

        return;
      }
      if (
        (tableRow.matchedIngredient.selectedStock.concentrationUnits.includes('%') &&
          tableRow.selectedUnit.name.includes('M')) ||
        (tableRow.matchedIngredient.selectedStock.concentrationUnits.includes('M') &&
          tableRow.selectedUnit.name.includes('%'))
      ) {
        showAlertSnackBar(
          EAlertType.WARNING,
          ESnackBarPosition.TOP,
          'Please correct the selection of concentration units.',
        );

        return;
      }
    }

    dispatch(applyDoeIngredients(doePreviewTableData));
    setShowModalFalse();
  }

  return (
    <>
      <div id='commandpanel-importdoe-importdoe-container'
        className="flex-y-center w-100pr align-items-center"
        style={{ ..._styles.rowTopMargin, ..._styles.parentContainer }}
      >
        <div id='commandpanel-importdoe-importdoe-header' style={{ ..._styles.firstColumn, ..._styles.headingText }}>Import DoE</div>

        <div id='commandpanel-importdoe-importdoe-actions' style={_styles.secondColumn}>
          <RMCustomButton
            iconProps={importIconProps}
            onClick={addAttachmentHandler}
            contentContainerClass={'w-[11%] add-linked-layer-button'}
            isDisabled={isScreenInUse}
            iconClass="h-[20px] w-[20px] items-center"
          ></RMCustomButton>
          <input
            type="file"
            accept={'.csv'}
            ref={inputFileRef}
            style={{ margin: '0px 10px', display: 'none' }}
            onInput={attachmentChangedHandler}
          />
        </div>
      </div>
    </>
  );
};

export default ImportDoe;

const _styles: IStyle = {
  parentContainer: {
    height: '2.125rem',
  },
  firstColumn: {
    width: '25%',
  },
  secondColumn: {
    width: '75%',
  },
  thirdColumn: {
    width: '62%',
  },
  rowTopMargin: {
    marginTop: '2%',
  },
  titleText: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    alignItems: 'center',
    justifyContent: 'flex-start',
    whiteSpace: 'nowrap',
  },
  secondRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '2.125rem',
  },
  volumeInput: {
    width: '100%',
    height: '2.125rem',
    border: 'none',
    paddingLeft: '9px',
  },
  inputWithPostFix: {
    border: 'solid 1px',
    width: '40%',
  },
  postFixContainer: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gridStepsInput: {
    width: '20%',
  },
  headingText: {
    fontSize: '0.8125rem',
    fontWeight: 'bold',
    alignItems: 'center',
    justifyContent: 'flex-start',
    whiteSpace: 'nowrap',
  },
  advanceOptionsContainer: {
    marginLeft: '1%',
  },
};
